import { z } from 'zod';
import { DeviceInfoSchema } from '../config/misc';
import { TournamentPoliciesSchema } from '../config/policies';
import { CategoryFilterSchema } from '../config/registration';
import { SignOffMethod, SignOffStatus } from '../enums';
import { ContestLeaderSchema } from './leaderboard';

/**
 * Offline queue operations (caddy / self-entry scorer).
 * The client generates opId (UUID v4/v7) when the user confirms an input, stores the op in IndexedDB,
 * and POSTs batches to /api/v1/scorer/ops until acknowledged. The server is idempotent on opId:
 * the same opId from the same scorer (caddy/participant, any session) with the same payload returns
 * the stored result as `duplicate`; a different payload or scorer returns `rejected OP_ID_REUSED`.
 */
export const ScoreSetOpSchema = z.object({
  opId: z.uuid(),
  kind: z.literal('score.set'),
  clientRecordedAt: z.iso.datetime({ offset: true }),
  payload: z.object({
    roundId: z.uuid(),
    /** exactly one of participantId / teamId */
    participantId: z.uuid().nullable().optional(),
    teamId: z.uuid().nullable().optional(),
    holeNumber: z.int().min(1).max(18),
    /** null clears the hole (allowed only within the edit window; the row is kept, audit CLEAR) */
    strokes: z.int().min(1).max(30).nullable(),
    putts: z.int().min(0).max(10).nullable().optional(),
    pickedUp: z.boolean().optional(),
    /** Score.version the device last saw (null = believes hole is empty) */
    baseVersion: z.int().nullable().optional(),
  }),
});

export const ContestEntryOpSchema = z.object({
  opId: z.uuid(),
  kind: z.literal('contest.entry'),
  clientRecordedAt: z.iso.datetime({ offset: true }),
  payload: z.object({
    contestId: z.uuid(),
    /** round the shot was played in (the scorer's current round); must be IN_PROGRESS */
    roundId: z.uuid(),
    bibNumber: z.int().min(0),
    distanceMeters: z.number().positive().max(600),
    /** 시각: when measured (device clock; audit only — ties are broken by server time) */
    measuredAt: z.iso.datetime({ offset: true }),
  }),
});

/** Caddy voids ITS OWN entry (wrong bib) within policies.editWindowMinutes, round not LOCKED. */
export const ContestVoidOpSchema = z.object({
  opId: z.uuid(),
  kind: z.literal('contest.void'),
  clientRecordedAt: z.iso.datetime({ offset: true }),
  payload: z.object({
    entryId: z.uuid(),
    reason: z.string().trim().min(1).max(500),
  }),
});

export const ScorerOpSchema = z.discriminatedUnion('kind', [
  ScoreSetOpSchema,
  ContestEntryOpSchema,
  ContestVoidOpSchema,
]);
export type ScorerOp = z.infer<typeof ScorerOpSchema>;

export const ScorerOpsRequestSchema = z.object({
  device: DeviceInfoSchema,
  /** true when these ops were queued while offline */
  replayed: z.boolean().default(false),
  ops: z.array(ScorerOpSchema).min(1).max(200),
});
export type ScorerOpsRequest = z.infer<typeof ScorerOpsRequestSchema>;

/** Rejection codes a scorer op can return (rejected status). */
export const ScorerRejectCode = z.enum([
  'VALIDATION',
  'NOT_ASSIGNED', // competitor / bib not in the scorer's groups for that round
  'NOT_ELIGIBLE', // contest eligibility (CategoryFilter) fails
  'ROUND_NOT_STARTED',
  'ROUND_LOCKED',
  'EDIT_WINDOW_EXPIRED',
  'ADMIN_LOCKED', // cell was corrected by an admin: only admins may change it
  'OP_ID_REUSED',
  'NOT_OWN_ENTRY', // contest.void of another scorer's entry
  'NOT_FOUND',
]);
export type ScorerRejectCode = z.infer<typeof ScorerRejectCode>;

/**
 * Per-op outcome. Client rules:
 *  applied | duplicate  -> remove from queue, mark "saved"
 *  conflict             -> remove, show server value (server wins), flag hole for review
 *  rejected             -> remove, show error code (ScorerRejectCode)
 *  (network error / 5xx / 429) -> keep in queue, retry with backoff
 */
export const OpResultSchema = z.object({
  opId: z.uuid(),
  status: z.enum(['applied', 'duplicate', 'conflict', 'rejected']),
  code: z.string().optional(),
  message: z.string().optional(),
  /** current server state of the affected record */
  score: z
    .object({
      roundId: z.uuid(),
      competitorId: z.string(),
      holeNumber: z.number(),
      strokes: z.number().nullable(),
      putts: z.number().nullable(),
      pickedUp: z.boolean(),
      source: z.enum(['CADDY', 'ADMIN', 'SELF', 'IMPORT']),
      version: z.number(),
    })
    .optional(),
  entry: z
    .object({
      entryId: z.uuid(),
      contestId: z.uuid(),
      roundId: z.uuid(),
      status: z.enum(['VALID', 'VOIDED', 'SUPERSEDED']),
      isLeader: z.boolean(),
      /**
       * contest.entry only: the entry replaced this player's earlier VALID record of the same
       * (contest, round) (now SUPERSEDED). Absent from older servers and on contest.void.
       */
      supersededPrevious: z.boolean().optional(),
    })
    .optional(),
  /** soft warnings (e.g. ABOVE_PAR_THRESHOLD, CONTEST_OUT_OF_RANGE) — op is still applied */
  warnings: z.array(z.string()).optional(),
});
export type OpResult = z.infer<typeof OpResultSchema>;

export const ScorerOpsResponseSchema = z.object({
  results: z.array(OpResultSchema),
  serverTime: z.iso.datetime(),
});
export type ScorerOpsResponse = z.infer<typeof ScorerOpsResponseSchema>;

/**
 * What a pick-up (pickedUp hole, "X") means in this tournament — the caddy app states it before the
 * value is saved (§15 #440): DQ = stroke play with incompleteHole 'DQ' (the player is disqualified
 * in that ranking), MAX_SCORE = the hole counts the maximum score, ZERO_POINTS = Stableford (0
 * points), LOSE_HOLE = match play (the hole is lost), DOUBLE_PAR = scramble team score.
 */
export const ScorerPickUpRule = z.enum([
  'DQ',
  'MAX_SCORE',
  'ZERO_POINTS',
  'LOSE_HOLE',
  'DOUBLE_PAR',
]);
export type ScorerPickUpRule = z.infer<typeof ScorerPickUpRule>;

const STROKE_VIEWS: ReadonlySet<string> = new Set(['STROKE_GROSS', 'STROKE_NET']);

/**
 * The pick-up rule of an effective scoring config: DQ whenever a stroke-play ranking of the
 * tournament (the primary format or an additional view) disqualifies an incomplete hole; otherwise
 * the primary format's meaning. Match / scramble keep their own meaning (their stroke view is a
 * by-product).
 */
export function pickUpRuleOf(config: {
  format: { kind: string };
  additionalViews?: readonly string[];
  strokePlay?: { incompleteHole: 'DQ' | 'MAX_SCORE' } | null;
}): ScorerPickUpRule {
  const kind = config.format.kind;
  if (kind.startsWith('MATCH_')) return 'LOSE_HOLE';
  if (kind === 'SCRAMBLE') return 'DOUBLE_PAR';
  const dq = (config.strokePlay?.incompleteHole ?? 'DQ') === 'DQ';
  const strokeRanking =
    STROKE_VIEWS.has(kind) || (config.additionalViews ?? []).some((v) => STROKE_VIEWS.has(v));
  if (dq && strokeRanking) return 'DQ';
  if (kind === 'STABLEFORD') return 'ZERO_POINTS';
  return 'MAX_SCORE';
}

/** The pick-up rule a scorer context says, or (older servers) the format default. */
export function scorerPickUpRule(tournament: {
  formatKind: string;
  pickUpRule?: ScorerPickUpRule | null;
}): ScorerPickUpRule {
  return tournament.pickUpRule ?? pickUpRuleOf({ format: { kind: tournament.formatKind } });
}

const ScorerHoleSchema = z.object({
  number: z.number(),
  par: z.number(),
  strokeIndex: z.number(),
  /** distance by teeBoxId */
  distances: z.record(z.string(), z.number().nullable()),
});

/** GET /api/v1/scorer/context — everything the caddy app needs (cached for offline). */
/**
 * A refused login on another phone that concerns this caddy (§15 #492): `NUMBER` = someone tried
 * this caddy's number for another player, `PLAYER` = caddy `code` tried to take this caddy's player
 * (`bib`). The caddy screen shows a banner: check the number, else log out. Kept 30 minutes.
 */
export const ScorerDuplicateLoginSchema = z.object({
  kind: z.enum(['NUMBER', 'PLAYER']),
  /** the caddy number used by the refused login */
  code: z.string(),
  /** the bib the refused login asked for */
  bib: z.number().int(),
  at: z.iso.datetime({ offset: true }),
});
export type ScorerDuplicateLogin = z.infer<typeof ScorerDuplicateLoginSchema>;

export const ScorerContextSchema = z.object({
  scorer: z.object({
    kind: z.enum(['CADDY', 'PLAYER']),
    id: z.uuid(),
    name: z.string(),
    code: z.string().nullable(),
  }),
  tournament: z.object({
    id: z.uuid(),
    name: z.string(),
    timezone: z.string(),
    formatKind: z.string(),
    logoUrl: z.string().nullable(),
    primaryColor: z.string(),
    /** what a pick-up means here (`pickUpRuleOf` of the effective scoring config); absent = older server */
    pickUpRule: ScorerPickUpRule.optional(),
  }),
  policies: TournamentPoliciesSchema,
  rounds: z.array(
    z.object({
      id: z.uuid(),
      roundNo: z.number(),
      date: z.string(),
      status: z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'LOCKED']),
      /** layouts played in this round by the scorer's groups (group.courseId -> courses[].courseId) */
      courses: z.array(
        z.object({
          courseId: z.uuid(),
          name: z.string(),
          /** holes of this round on this layout in CARD order (from the round-start snapshot) */
          holes: z.array(ScorerHoleSchema),
        }),
      ),
      groups: z.array(
        z.object({
          id: z.uuid(),
          number: z.number(),
          name: z.string().nullable(),
          courseId: z.uuid(),
          startHole: z.number(),
          teeTime: z.string().nullable(),
          /** active caddies on this group (additive, §15 #470): > 1 = 1 caddy per player */
          caddyCount: z.number().int().nonnegative().optional(),
          competitors: z.array(
            z.object({
              competitorId: z.uuid(),
              kind: z.enum(['INDIVIDUAL', 'TEAM']),
              name: z.string(),
              bib: z.number().nullable(),
              teeBoxId: z.uuid().nullable(),
              teeName: z.string().nullable(),
              /**
               * INDIVIDUAL: the participant's nationality code as entered (e.g. 'VN', 'KR'; flag
               * next to the name); null for teams or when unknown. Optional = older servers.
               */
              nationality: z.string().nullable().optional(),
              /** TEAM competitors: the team's players in this group (contest entry needs a bib) */
              members: z
                .array(
                  z.object({
                    participantId: z.uuid(),
                    name: z.string(),
                    bib: z.number().nullable(),
                  }),
                )
                .optional(),
            }),
          ),
        }),
      ),
      contests: z.array(
        z.object({
          id: z.uuid(),
          type: z.enum(['NEAREST_PIN', 'LONGEST_DRIVE']),
          /** layout the contest hole is on (null = every layout of the round) */
          courseId: z.uuid().nullable(),
          holeNumber: z.number(),
          name: z.string().nullable(),
          /** true: this caddy is a hole marshal for it (may enter any eligible player of the round) */
          marshal: z.boolean(),
          /** who may enter it (e.g. {gender: ['F']} = women's hole, §15 #488); additive */
          eligibility: CategoryFilterSchema.optional(),
          /**
           * players of this scorer's groups who may enter it; null = everyone (no filter, or a
           * marshal). Absent on older servers = everyone.
           */
          eligibleParticipantIds: z.array(z.uuid()).nullable().optional(),
        }),
      ),
    }),
  ),
  /** current server scores for the scorer's groups */
  scores: z.array(
    z.object({
      roundId: z.uuid(),
      competitorId: z.string(),
      holeNumber: z.number(),
      strokes: z.number().nullable(),
      putts: z.number().nullable(),
      pickedUp: z.boolean(),
      source: z.enum(['CADDY', 'ADMIN', 'SELF', 'IMPORT']),
      version: z.number(),
      /** Score.firstEnteredAt — edit window anchor */
      firstEnteredAt: z.iso.datetime().nullable(),
    }),
  ),
  /** the scorer's own contest entries (VALID/VOIDED/SUPERSEDED) so it can void within the window */
  contestEntries: z.array(
    z.object({
      entryId: z.uuid(),
      contestId: z.uuid(),
      roundId: z.uuid(),
      bibNumber: z.number(),
      distanceMeters: z.number(),
      status: z.enum(['VALID', 'VOIDED', 'SUPERSEDED']),
      createdAt: z.iso.datetime(),
      /** the player's name (a hole marshal's entries are outside its groups); absent on older servers */
      name: z.string().optional(),
    }),
  ),
  /**
   * current (contest, round) leader blocks of the contests in `rounds[].contests` — the same block
   * the caddy stream's contest.updated carries, so a freshly opened device shows the leader before
   * the next entry arrives. Absent on older servers.
   */
  contestLeaders: z.array(ContestLeaderSchema).optional(),
  /** a refused duplicate login on another phone (§15 #492); absent = none / older servers */
  duplicateLogin: ScorerDuplicateLoginSchema.nullable().optional(),
  /**
   * realtime version read before `contestLeaders` was built (the blocks reflect at least this
   * version). A client applies a context block only when this is ≥ the version of the last
   * contest.updated it applied for that (contest, round), and applies a contest.updated only when
   * its version is newer than the block it holds. Absent when there are no contests / older servers.
   */
  contestLeadersVersion: z.number().int().nonnegative().optional(),
  /** current sign-offs of the INDIVIDUAL players in the scorer's groups (survives a cold start) */
  signOffs: z
    .array(
      z.object({
        roundId: z.uuid(),
        participantId: z.uuid(),
        status: SignOffStatus,
        method: SignOffMethod,
      }),
    )
    .optional(),
  serverTime: z.iso.datetime(),
});
export type ScorerContext = z.infer<typeof ScorerContextSchema>;

/** POST /scorer/signoff — caddy-device sign-off; the signOffCode only when policies.signOffCode. */
export const ScorerSignOffRequestSchema = z.object({
  roundId: z.uuid(),
  participantId: z.uuid(),
  /**
   * 4-digit code from the player card / player portal (wrong 5x per round -> locked for device).
   * Required only when the tournament's policies.signOffCode is on; ignored otherwise.
   */
  signOffCode: z
    .string()
    .regex(/^\d{4}$/)
    .optional(),
  confirm: z.boolean(),
  disputeNote: z.string().max(1000).nullable().optional(),
});
export type ScorerSignOffRequest = z.infer<typeof ScorerSignOffRequestSchema>;
